<script lang="ts">
  import { Folder, FolderOpen, Plus, Upload, AlertCircle, BookOpen } from 'lucide-svelte'
  import { vaultStore } from '$lib/stores/vaultStore'
  import { fileStore } from '$lib/stores/fileStore'
  import { onMount } from 'svelte'
  import { BrowserCompatibility } from '$lib/services/BrowserCompatibility'
  import { UnifiedFileService } from '$lib/services/UnifiedFileService'
  import hanLogoLight from '../assets/1024x1024_han.png'
  import hanLogoDark from '../assets/1024-han-white.png'
  
  interface Props {
    onVaultSelected: () => void
  }
  
  let { onVaultSelected }: Props = $props()
  
  interface RecentVault {
    name: string
    path: string
  }
  
  let recentVaults = $state<RecentVault[]>([])
  let isDarkMode = $state(false)
  let browserInfo = BrowserCompatibility.getBrowserInfo()
  let isUsingFallback = UnifiedFileService.isUsingFallback()
  let showCompatibilityWarning = $state(false)
  
  // Load recent vaults from localStorage
  $effect(() => {
    const saved = localStorage.getItem('korean-grammar-recent-vaults')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Handle both old string[] format and new RecentVault[] format
        if (Array.isArray(parsed)) {
          if (parsed.length > 0 && typeof parsed[0] === 'string') {
            // Old format - convert to new format (path will be empty)
            recentVaults = parsed.map(name => ({ name, path: '' }))
          } else {
            recentVaults = parsed
          }
        }
      } catch (e) {
        console.error('Failed to parse recent vaults:', e)
      }
    }
  })
  
  // Detect theme on mount
  onMount(() => {
    // Hide initial loading screen if it's still visible
    const initialLoading = document.getElementById('initial-loading')
    if (initialLoading) {
      initialLoading.classList.add('fade-out')
      setTimeout(() => {
        initialLoading.remove()
      }, 300)
    }
    
    // Set initial small window size
    if (window.api?.controlWindow) {
      window.api.controlWindow({ 
        action: 'resize', 
        width: 720, 
        height: 540 
      })
    }
    
    // Check for theme preference
    const detectTheme = () => {
      const root = document.documentElement
      if (root.classList.contains('dark-theme')) {
        isDarkMode = true
      } else if (root.classList.contains('light-theme')) {
        isDarkMode = false
      } else {
        // Auto mode - check system preference
        isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
      }
    }
    
    detectTheme()
    
    // Listen for theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleThemeChange = () => detectTheme()
    mediaQuery.addEventListener('change', handleThemeChange)
    
    // Listen for vault:clear-recent from menu
    if (window.electronAPI && window.electronAPI.on) {
      window.electronAPI.on('vault:clear-recent', () => {
        recentVaults = []
        localStorage.removeItem('korean-grammar-recent-vaults')
      })
      
      // Listen for vault:open-recent from menu
      window.electronAPI.on('vault:open-recent', async (vaultPath: string) => {
        try {
          if (window.api) {
            const result = await window.api.openVault(vaultPath)
            if (result.success && result.path) {
              const vaultName = result.path.split('/').pop() || result.path.split('\\').pop() || 'vault'
              const fakeHandle = {
                kind: 'directory',
                name: vaultName,
                path: result.path
              } as any
              
              await vaultStore.setVault(fakeHandle)
              saveRecentVault(vaultName, result.path)
              handleVaultSelection()
            }
          }
        } catch (error) {
          console.error('Failed to open recent vault:', error)
        }
      })
    }
    
    return () => {
      mediaQuery.removeEventListener('change', handleThemeChange)
    }
  })
  
  async function handleVaultSelection() {
    // Restore window size when vault is selected
    if (window.api?.controlWindow) {
      await window.api.controlWindow({ 
        action: 'resize', 
        width: 1200, 
        height: 800 
      })
    }
    onVaultSelected()
  }
  
  async function createNewVault() {
    try {
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      // Check for Safari/iOS fallback mode
      if (!isElectron && isUsingFallback) {
        showCompatibilityWarning = true
        return
      }
      
      if (isElectron) {
        // Use Electron IPC to select vault directory
        const lastDir = getLastDirectory()
        const result = await (window as any).api.selectVault(lastDir ? { defaultPath: lastDir } : undefined)
        
        if (result.success && result.path) {
          // Create a fake FileSystemDirectoryHandle for compatibility with actual path
          const fakeHandle = {
            kind: 'directory',
            name: result.path.split('/').pop() || result.path.split('\\').pop() || 'vault',
            path: result.path  // Store the actual full path
          } as any
          
          await vaultStore.setVault(fakeHandle)
          saveRecentVault(fakeHandle.name, result.path)
          saveLastDirectory(result.path)
          handleVaultSelection()
        }
      } else {
        // @ts-ignore - File System Access API
        const dirHandle = await window.showDirectoryPicker({
          mode: 'readwrite',
          startIn: 'documents'
        })
        
        await vaultStore.setVault(dirHandle)
        saveRecentVault(dirHandle.name, dirHandle.name)
        handleVaultSelection()
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to create vault:', err)
      }
    }
  }
  
  async function openExistingVault(specificPath?: string) {
    try {
      const isElectron = typeof window !== 'undefined' && (window as any).api
      
      // Check for Safari/iOS fallback mode
      if (!isElectron && isUsingFallback) {
        showCompatibilityWarning = true
        return
      }
      
      if (isElectron) {
        if (specificPath) {
          // Open specific path directly
          const result = await (window as any).api.openVault(specificPath)
          
          if (result.success && result.path) {
            const fakeHandle = {
              kind: 'directory',
              name: result.path.split('/').pop() || result.path.split('\\').pop() || 'vault',
              path: result.path
            } as any
            
            await vaultStore.setVault(fakeHandle)
            saveRecentVault(fakeHandle.name, result.path)
            saveLastDirectory(result.path)
            handleVaultSelection()
          }
        } else {
          // Use Electron IPC to select vault directory with last directory
          const lastDir = getLastDirectory()
          const result = await (window as any).api.selectVault(lastDir ? { defaultPath: lastDir } : undefined)
          
          if (result.success && result.path) {
            const fakeHandle = {
              kind: 'directory',
              name: result.path.split('/').pop() || result.path.split('\\').pop() || 'vault',
              path: result.path
            } as any
            
            await vaultStore.setVault(fakeHandle)
            saveRecentVault(fakeHandle.name, result.path)
            saveLastDirectory(result.path)
            handleVaultSelection()
          }
        }
      } else {
        // @ts-ignore - File System Access API
        const dirHandle = await window.showDirectoryPicker({
          mode: 'readwrite'
        })
        
        await vaultStore.setVault(dirHandle)
        saveRecentVault(dirHandle.name, dirHandle.name)
        handleVaultSelection()
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to open vault:', err)
      }
    }
  }
  
  function saveRecentVault(name: string, path: string) {
    const updated = [
      { name, path },
      ...recentVaults.filter(v => v.path !== path)
    ].slice(0, 5)
    recentVaults = updated
    localStorage.setItem('korean-grammar-recent-vaults', JSON.stringify(updated))
    
    // Notify main process to update menu
    if (window.electronAPI) {
      const vaultPaths = updated.map(v => v.path)
      window.electronAPI.send('update-recent-vaults', vaultPaths)
    }
  }
  
  function saveLastDirectory(path: string) {
    try {
      // Extract parent directory from path
      const pathSegments = path.split(/[\\/]/)
      if (pathSegments.length > 1) {
        pathSegments.pop() // Remove the last segment (the selected folder)
        const parentPath = pathSegments.join('/')
        localStorage.setItem('korean-grammar-last-directory', parentPath)
      }
    } catch (e) {
      console.error('Failed to save last directory:', e)
    }
  }
  
  function getLastDirectory(): string | null {
    return localStorage.getItem('korean-grammar-last-directory')
  }
  
  async function openFilesFallback() {
    try {
      const result = await UnifiedFileService.openFile()
      if (result && result.content) {
        // Create a fake vault for compatibility
        const fakeVault = {
          kind: 'directory',
          name: 'Safari Files',
          isFallback: true,
          files: [{
            name: result.name || 'untitled.md',
            content: result.content
          }]
        } as any

        await vaultStore.setVault(fakeVault)

        // Open the file in the editor
        const fileName = result.name || 'untitled.md'
        const fileTitle = fileName.replace(/\.md$/, '') || 'Untitled'

        await fileStore.createTabFromFallback({
          id: fileName,
          title: fileTitle,
          content: result.content,
          path: fileName,
          isModified: false,
          isFallback: true
        })

        handleVaultSelection()
      }
    } catch (error) {
      console.error('Failed to open file:', error)
      alert('파일을 열 수 없습니다. 다시 시도해주세요.')
    }
  }

  async function openGuideVault() {
    try {
      const isElectron = typeof window !== 'undefined' && (window as any).api

      if (!isElectron) {
        // Web version doesn't support guide vault
        alert('가이드 보관소는 데스크톱 앱에서만 사용할 수 있습니다.')
        return
      }

      // Create/get guide vault
      const result = await (window as any).api.createGuideVault()

      if (result.success && result.path) {
        // Open the guide vault
        const vaultName = result.path.split('/').pop() || result.path.split('\\').pop() || 'guide-vault'
        const fakeHandle = {
          kind: 'directory',
          name: vaultName,
          path: result.path
        } as any

        await vaultStore.setVault(fakeHandle)
        saveRecentVault('가이드', result.path)
        saveLastDirectory(result.path)
        handleVaultSelection()
      } else {
        alert('가이드 보관소를 만들 수 없습니다: ' + (result.error || '알 수 없는 오류'))
      }
    } catch (error) {
      console.error('Failed to open guide vault:', error)
      alert('가이드 보관소를 열 수 없습니다.')
    }
  }
</script>

<div class="vault-selector" class:dark-mode={isDarkMode}>
  <!-- Draggable header area (invisible) -->
  <div class="drag-header"></div>
  
  <div class="vault-content">
    <div class="vault-logo">
      <img 
        src={isDarkMode ? hanLogoDark : hanLogoLight} 
        alt="Korean Grammar Checker Logo"
        width="64"
        height="64"
        class="app-logo"
      />
    </div>
    
    <h1>Korean Grammar Checker</h1>
    <p class="subtitle">시작하기</p>
    
    <div class="vault-actions">
      <button class="action-button primary" onclick={() => createNewVault()}>
        <div class="action-icon">
          <Plus size={24} />
        </div>
        <div class="action-content">
          <div class="action-title">새 보관소 만들기</div>
          <div class="action-description">새 폴더에 문서를 저장합니다</div>
        </div>
      </button>
      
      <button class="action-button" onclick={() => openExistingVault()}>
        <div class="action-icon">
          <FolderOpen size={24} />
        </div>
        <div class="action-content">
          <div class="action-title">폴더를 보관소로 열기</div>
          <div class="action-description">기존 폴더를 사용합니다</div>
        </div>
      </button>

      {#if typeof window !== 'undefined' && (window as any).api}
        <button class="action-button guide" onclick={openGuideVault}>
          <div class="action-icon">
            <BookOpen size={24} />
          </div>
          <div class="action-content">
            <div class="action-title">가이드 보기</div>
            <div class="action-description">예제 문서로 시작합니다</div>
          </div>
        </button>
      {/if}
    </div>
    
    {#if isUsingFallback}
      <div class="fallback-actions">
        <div class="browser-warning">
          <AlertCircle size={20} />
          <span>{browserInfo.name}에서는 폴더 접근이 제한됩니다</span>
        </div>
        <button class="action-button fallback" onclick={openFilesFallback}>
          <div class="action-icon">
            <Upload size={24} />
          </div>
          <div class="action-content">
            <div class="action-title">파일 열기</div>
            <div class="action-description">개별 파일을 선택합니다</div>
          </div>
        </button>
        <p class="fallback-info">
          전체 기능을 사용하려면 Chrome, Edge 또는 데스크톱 앱을 이용해주세요.
        </p>
      </div>
    {/if}
    
    {#if recentVaults.length > 0}
      <div class="recent-vaults">
        <div class="recent-header">
          <span class="recent-title">최근 보관소</span>
          <span class="recent-count">{recentVaults.length}</span>
        </div>
        <div class="recent-list">
          {#each recentVaults as vault}
            <button 
              class="recent-item" 
              onclick={() => openExistingVault(vault.path)}
              title={vault.path || vault.name}
            >
              <Folder size={14} />
              <span class="vault-name">{vault.name}</span>
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</div>

{#if showCompatibilityWarning}
  <div
    class="modal-overlay"
    role="presentation"
    onclick={() => showCompatibilityWarning = false}
    onkeydown={(e) => e.key === 'Escape' && (showCompatibilityWarning = false)}
    tabindex="-1"
  >
    <div
      class="modal-content"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      tabindex="0"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      <div class="modal-header">
        <AlertCircle size={24} color="#FF9500" />
        <h2 id="modal-title">브라우저 호환성 안내</h2>
      </div>
      <div class="modal-body">
        <p><strong>{browserInfo.name}</strong>에서는 파일 시스템 접근이 제한됩니다.</p>
        
        <div class="feature-list">
          <h3>✅ 사용 가능한 기능:</h3>
          <ul>
            <li>파일 열기 (업로드)</li>
            <li>파일 저장 (다운로드)</li>
            <li>문법 검사</li>
          </ul>
        </div>
        
        <div class="feature-list limited">
          <h3>❌ 제한된 기능:</h3>
          <ul>
            <li>폴더 열기</li>
            <li>실시간 파일 감시</li>
            <li>자동 저장</li>
          </ul>
        </div>
        
        <p class="recommendation">
          전체 기능을 사용하시려면 <strong>Chrome</strong>, <strong>Edge</strong> 브라우저 또는 <strong>데스크톱 앱</strong>을 이용해주세요.
        </p>
      </div>
      <div class="modal-footer">
        <button class="modal-button primary" onclick={openFilesFallback}>
          파일 열기로 계속
        </button>
        <button class="modal-button" onclick={() => showCompatibilityWarning = false}>
          닫기
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .vault-selector {
    position: fixed;
    inset: 0;
    background: var(--bg-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  }
  
  /* Drag header for window movement (invisible) */
  .drag-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 40px;
    -webkit-app-region: drag;
    user-select: none;
    z-index: 10001;
  }
  
  /* Dark mode styles */
  .vault-selector.dark-mode {
    background: #0a0a0a;
  }
  
  .vault-selector.dark-mode h1 {
    color: #e5e5e5;
  }
  
  .vault-selector.dark-mode .subtitle {
    color: #999999;
  }
  
  .vault-selector.dark-mode .action-button {
    background: #111111;
    border-color: #2a2a2a;
    color: #e5e5e5;
  }
  
  .vault-selector.dark-mode .action-button:hover {
    background: #1a1a1a;
    border-color: #3a3a3a;
  }
  
  .vault-selector.dark-mode .action-button.primary {
    background: #7c7aff;
    border-color: #7c7aff;
    color: white;
  }
  
  .vault-selector.dark-mode .action-button.primary:hover {
    background: #a78bfa;
    border-color: #a78bfa;
  }
  
  .vault-selector.dark-mode .action-title {
    color: #e5e5e5;
  }
  
  .vault-selector.dark-mode .action-description {
    color: #999999;
  }
  
  .vault-selector.dark-mode .recent-header {
    color: #999999;
  }
  
  .vault-selector.dark-mode .recent-item {
    color: #e5e5e5;
    border-color: transparent;
  }
  
  .vault-selector.dark-mode .recent-item:hover {
    background: #1a1a1a;
  }
  
  .vault-selector.dark-mode .recent-list {
    border-color: #2a2a2a;
    background: #111111;
  }
  
  .vault-content {
    max-width: 560px;
    width: 90%;
    padding: 48px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .vault-logo {
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
  }
  
  .app-logo {
    opacity: 0.95;
    transition: opacity 0.2s ease;
  }
  
  .app-logo:hover {
    opacity: 1;
  }
  
  h1 {
    font-size: 22px;
    font-weight: 400;
    color: var(--text-primary);
    margin: 0 0 6px 0;
    letter-spacing: -0.3px;
  }
  
  .subtitle {
    font-size: 12px;
    color: var(--text-secondary);
    margin: 0 0 32px 0;
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 500;
  }
  
  .vault-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    margin-bottom: 32px;
  }
  
  .action-button {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 18px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s ease;
    -webkit-app-region: no-drag;
    width: 100%;
  }
  
  .action-button:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
    transform: translateX(1px);
  }
  
  .action-button.primary {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }
  
  .action-button.primary:hover {
    background: var(--accent-secondary);
    border-color: var(--accent-secondary);
  }
  
  .action-button.primary .action-title {
    color: white;
  }
  
  .action-button.primary .action-description {
    color: rgba(255, 255, 255, 0.8);
  }

  .action-button.guide {
    border-color: var(--accent-primary);
    border-width: 1.5px;
  }

  .action-button.guide:hover {
    background: rgba(124, 122, 255, 0.1);
    border-color: var(--accent-secondary);
  }

  .dark-mode .action-button.guide:hover {
    background: rgba(124, 122, 255, 0.15);
  }

  .action-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }
  
  .action-content {
    flex: 1;
  }
  
  .action-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    margin-bottom: 2px;
  }
  
  .action-description {
    font-size: 12px;
    color: var(--text-tertiary);
    opacity: 0.8;
  }
  
  .recent-vaults {
    width: 100%;
  }
  
  .recent-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    padding: 0 4px;
  }
  
  .recent-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.7;
  }
  
  .recent-count {
    font-size: 11px;
    color: var(--text-tertiary);
    opacity: 0.5;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }
  
  .recent-list {
    display: flex;
    flex-direction: column;
    max-height: 150px;
    overflow-y: auto;
    border: 1px solid var(--border-primary);
    border-radius: 6px;
    padding: 4px;
    background: var(--bg-secondary);
  }
  
  /* Hide scrollbar for recent list */
  .recent-list {
    scrollbar-width: none; /* Firefox */
  }
  
  .recent-list::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Edge */
  }
  
  .recent-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-primary);
    cursor: pointer;
    font-size: 13px;
    text-align: left;
    transition: all 0.1s ease;
    -webkit-app-region: no-drag;
    min-height: 32px;
  }
  
  .recent-item:hover {
    background: var(--bg-secondary);
  }
  
  .recent-item:active {
    transform: scale(0.98);
  }
  
  .vault-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* Fallback mode styles */
  .fallback-actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    margin-top: 24px;
  }
  
  .browser-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: rgba(255, 149, 0, 0.1);
    border: 1px solid rgba(255, 149, 0, 0.3);
    border-radius: 6px;
    color: #FF9500;
    font-size: 13px;
    font-weight: 500;
  }
  
  .dark-mode .browser-warning {
    background: rgba(255, 149, 0, 0.15);
    border-color: rgba(255, 149, 0, 0.4);
  }
  
  .action-button.fallback {
    background: var(--bg-secondary);
    border: 2px dashed var(--border-primary);
  }
  
  .action-button.fallback:hover {
    border-color: var(--accent-primary);
    background: var(--bg-tertiary);
  }
  
  .fallback-info {
    margin: 8px 0 0;
    font-size: 12px;
    color: var(--text-secondary);
    text-align: center;
    line-height: 1.5;
  }
  
  /* Modal styles */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10002;
    backdrop-filter: blur(2px);
  }
  
  .modal-content {
    background: var(--bg-primary);
    border-radius: 12px;
    max-width: 480px;
    width: 90%;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-primary);
  }
  
  .modal-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border-primary);
  }
  
  .modal-header h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .modal-body {
    padding: 24px;
    color: var(--text-primary);
  }
  
  .modal-body p {
    margin: 0 0 16px;
    line-height: 1.5;
    font-size: 14px;
  }
  
  .feature-list {
    margin: 16px 0;
  }
  
  .feature-list h3 {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .feature-list ul {
    margin: 0 0 16px;
    padding-left: 20px;
    list-style: none;
  }
  
  .feature-list li {
    margin: 4px 0;
    font-size: 13px;
    color: var(--text-secondary);
  }
  
  .feature-list.limited h3 {
    color: #FF6B6B;
  }
  
  .recommendation {
    margin: 20px 0 0;
    padding: 12px;
    background: var(--bg-secondary);
    border-radius: 6px;
    font-size: 13px;
  }
  
  .recommendation strong {
    color: var(--accent-primary);
  }
  
  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid var(--border-primary);
  }
  
  .modal-button {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    color: var(--text-primary);
  }
  
  .modal-button:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
  }
  
  .modal-button.primary {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }
  
  .modal-button.primary:hover {
    background: var(--accent-secondary);
    border-color: var(--accent-secondary);
  }
  
  @media (max-width: 640px) {
    .vault-content {
      padding: 32px 20px;
    }
  }
</style>